import CheckCircle from "@assets/images/check-circle.svg";
import InfoIcon from "@assets/images/info.png";
import XCircle from "@assets/images/x-circle.svg";
import { Box } from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import {
  DomesticOrderConfirmationFilterInput,
  InquiryOrderConfirmationStatus,
  SapOrderConfirmationInput,
  useListOrderConfirmationSapQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginationLocal from "@saleor/hooks/usePaginationLocal";
import { makeStyles } from "@saleor/macaw-ui";
import OrderConfirmListTable from "@saleor/OrderConfirm/components/OrderConfirmListTable";
import OrderFilterSectionAdmin from "@saleor/OrderConfirm/components/OrderFilterSectionAdmin";
import {
  OrderConfirmListSortField,
  OrderConfirmListUrl,
  OrderConfirmUrlQueryParams
} from "@saleor/OrderConfirm/urls";
import { ListViews } from "@saleor/types";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

export interface IOrderConfirmProps {
  params: OrderConfirmUrlQueryParams;
}

const useStyles = makeStyles(
  theme => ({
    textError: {
      color: theme.palette.error.light
    }
  }),
  { name: "OrdersView" }
);

function SectionMessage(props) {
  return (
    <Box display={"flex"} justifyItems="center">
      {props.icon && <img src={props.icon} alt="info" />}
      <Box ml={1}>
        <MyTypography
          variant="body3"
          style={{
            color: props.color
          }}
          color={props.color}
        >
          {props.message}
        </MyTypography>
      </Box>
    </Box>
  );
}

const OrderConfirmViewAdmin: React.FC<IOrderConfirmProps> = ({ params }) => {
  const [filter, setFilter] = useState<DomesticOrderConfirmationFilterInput>(
    {}
  );
  const [filterSAP, setFilterSAP] = useState<SapOrderConfirmationInput>(
    undefined
  );
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const intl = useIntl();
  const classes = useStyles();
  const navigate = useNavigator();
  const selectSendMail = useBulkActions();

  const [
    { filterOrderConfirm: filterAppState },
    dispatchAppState
  ] = useAppState();

  useEffect(() => {
    if (filterAppState && Object.keys(filterAppState).length) {
      setFilter(filterAppState);
      setIsSearched(true);
    }
  }, []);

  const { updateListSettings, settings } = useListSettings(
    ListViews.ORDER_CONFIRM_ADMIN
  );

  const mapProductGroup = productArr => {
    if (!productArr || !productArr.length) {
      return undefined;
    }
    return productArr;
  };

  const handleChangeFilter = filterObj => {
    if (Object.keys(filterObj).length === 0) {
      setFilter({});
      dispatchAppState({
        type: "setFilterOrderConfirm",
        payload: {
          filterOrderConfirm: {}
        }
      });
      setIsSearched(false);
      return;
    }
    const data = {
      bu: filterObj?.bu,
      soNo: filterObj.soNo || undefined,
      soldTo: filterObj.soldTo?.length ? filterObj.soldTo : undefined,
      poNo: filterObj.poNo || undefined,
      material: filterObj.material?.length ? filterObj.material : undefined,
      distributionChannel: filterObj.channel || undefined,
      saleOrganization: filterObj.saleOrganization
        ? [filterObj.saleOrganization]
        : undefined,
      status: filterObj.status?.length ? filterObj.status : undefined,
      materialGroup: filterObj.productGroup?.length
        ? filterObj.productGroup
        : undefined,
      orderCreateDate: filterObj.orderCreateDate || undefined
    };
    setIsSearched(true);
    dispatchAppState({
      type: "setFilterOrderConfirm",
      payload: {
        filterOrderConfirm: {
          ...data,
          saleOrganization: data.saleOrganization?.[0]
        }
      }
    });
    selectSendMail.reset();
    setFilter({ ...data });
  };

  useEffect(() => {
    const isRequiredSaleOrg = !filter.poNo && !filter.soNo;
    const shouldUseSaleOrgValue = isRequiredSaleOrg || filter.saleOrganization;

    const _filterSAP: SapOrderConfirmationInput = {
      bu: filter?.bu,
      soldTo: filter.soldTo ? filter.soldTo : undefined,
      materialNoMaterialDescription: filter.material
        ? filter.material
        : undefined,
      createDate:
        filter?.orderCreateDate?.gte && filter?.orderCreateDate?.lte
          ? {
              gte: filter.orderCreateDate?.gte,
              lte: filter.orderCreateDate?.lte
            }
          : undefined,
      channel: filter.distributionChannel,
      saleOrg: shouldUseSaleOrgValue ? filter.saleOrganization : ["All"],
      productGroup: mapProductGroup(filter.materialGroup),
      poNo: filter.poNo,
      soNo: filter.soNo,
      status: (filter.status as unknown) as InquiryOrderConfirmationStatus
    };
    setFilterSAP(_filterSAP);
  }, [filter]);

  const {
    data: dataSearch,
    loading: loadingSearch
  } = useListOrderConfirmationSapQuery({
    displayLoader: true,
    variables: {
      filter: filterSAP
    },
    skip:
      !filterSAP ||
      (filterSAP && !Object.values(filterSAP).filter(value => value).length)
  });
  const paramSort = getSortParams(params);
  const dataSearchList = useMemo(() => {
    if (dataSearch?.listOrderConfirmationSap) {
      const listToSort = [...dataSearch.listOrderConfirmationSap];
      switch (paramSort.sort) {
        case OrderConfirmListSortField.soNo:
          if (paramSort.asc) {
            listToSort.sort((itemA, itemB) =>
              itemA.soNo.localeCompare(itemB.soNo)
            );
          } else {
            listToSort.sort((itemA, itemB) =>
              itemB.soNo.localeCompare(itemA.soNo)
            );
          }
      }
      return listToSort;
    }
    return [];
  }, [dataSearch?.listOrderConfirmationSap, filterSAP, paramSort?.asc]);

  const {
    currentData,
    next,
    prev,
    toFirst,
    toLast,
    disableNext,
    disablePrev,
    lastestPageItemNumber
  } = usePaginationLocal(dataSearchList, settings.rowNumber);

  const handleSort = field => {
    toFirst();
    navigate(
      OrderConfirmListUrl({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };

  useEffect(() => {
    toFirst();
  }, [settings?.rowNumber, filter]);

  const message = useMemo(() => {
    if (loadingSearch) {
      return {
        icon: "",
        message: "",
        color: ""
      };
    }
    if (!isSearched) {
      return {
        icon: InfoIcon,
        message: (
          <>
            โปรดกรอกข้อมูลที่จำเป็น (<b className={classes.textError}>*</b>)
            และกดค้นหา
          </>
        ),
        color: "#555555"
      };
    }
    if (isSearched && !dataSearch?.listOrderConfirmationSap?.length) {
      return {
        icon: XCircle,
        message: intl.formatMessage({
          defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
        }),
        color: "#FF2600"
      };
    }
    return {
      icon: CheckCircle,
      message: intl.formatMessage({
        defaultMessage: "พบรายการที่เกี่ยวข้อง"
      }),
      color: "#28A745"
    };
  }, [dataSearch?.listOrderConfirmationSap?.length, isSearched, loadingSearch]);

  return (
    <>
      <OrderFilterSectionAdmin
        isSearched={isSearched}
        message={<SectionMessage {...message} />}
        handleChangeFilter={handleChangeFilter}
        listOrder={dataSearchList}
        loading={loadingSearch}
      />

      {!loadingSearch && dataSearchList.length > 0 ? (
        <OrderConfirmListTable
          selectSendMail={selectSendMail}
          settings={settings}
          disabled={loadingSearch}
          data={currentData()}
          dataSearchList={dataSearchList}
          onNextPage={next}
          onPreviousPage={prev}
          onUpdateListSettings={updateListSettings}
          onNextLastPage={toLast}
          onPreviousFirstPage={toFirst}
          totalCount={dataSearch?.listOrderConfirmationSap?.length}
          latestPageItemNumber={lastestPageItemNumber}
          pageInfo={{
            hasPreviousPage: !disablePrev(),
            hasNextPage: !disableNext()
          }}
          loading={loadingSearch}
          onSort={handleSort}
          sort={getSortParams(params)}
          onRowClick={() => null}
          filter={filter}
        />
      ) : null}
    </>
  );
};

export default OrderConfirmViewAdmin;
